/// <amd-module name="Test/TestResultsSettings/Components/TestResultsFlakySettingsView" />
import * as React from "react";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import * as Telemetry from "Test/common-util/Telemetry";
import * as TelemetryConstants from "Test/common-util/TelemetryConstants";
import { FadeToast } from "Test/TestResultsSettings/Components/FadeToast";
import * as Resources from "Test/TestResultsSettings/Resources";
import { ITestResultsSettingsState, TestResultsSettingsStore } from "Test/TestResultsSettings/Store/TestResultsSettingsStore";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import * as Util_String from "VSS/Core/Util/String";
import { Components, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { CardContent, CustomCard } from "VSSUI/Card";
import { Checkbox } from "VSSUI/Checkbox";
import { Dialog } from "VSSUI/Dialog";
import { Dropdown } from "VSSUI/Dropdown";
import { CustomHeader, HeaderDescription, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { CustomHeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListItemDetails, ListItem, ListSelection, ScrollableList } from "VSSUI/List";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";
import { ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { Toggle } from "VSSUI/Toggle";
import { Tooltip } from "VSSUI/TooltipEx";
import { DropdownMultiSelection } from "VSSUI/Utilities/DropdownSelection";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ISelectionRange } from "VSSUI/Utilities/Selection";

export interface ITestSettingsProps {
    store: TestResultsSettingsStore;
    state: ITestResultsSettingsState;
 }

export class TestResultsFlakySettingsView extends VssComponent<ITestSettingsProps, ITestResultsSettingsState> {
    private _flakyPiplineSelection: DropdownMultiSelection;
    private _loadingItem: IListBoxItem<{}>;
    private _pipelineItems: ObservableArray<IListBoxItem<{}>>;
    private _store: TestResultsSettingsStore;
    private _selectedPipelinesArrayItemProvider = new ArrayItemProvider([] as IListBoxItem<{}>[]);
    private _isRemovePipelineDialogOpen = new ObservableValue<boolean>(false);
    private _selectedPipelineItem: IListBoxItem<{}>;
    private _optedSelectPipelineOption: boolean = false;
    private _selectedFlakyDetectionType: string;

    constructor(props: ITestSettingsProps, context: IVssComponentContext) {
        super(props, context);
        this._store = props.store;
        this.state = props.state;
        this._loadingItem = {
            id: Resources.LoadingText,
            type: ListBoxItemType.Loading
        };
        this._flakyPiplineSelection = new DropdownMultiSelection();
        this._pipelineItems = new ObservableArray<IListBoxItem<{}>>();
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._store.addChangedListener(this._handleStoreChange);
        this.state.settingsToggleButton.settingsObservable.subscribe(this._settingsToggleButtonChanged);
        this.state.settingsTypeState.settingsObservable.subscribe(this._settingsDetectionTypeStateChanged);
        this.state.selectedPipeline.settingsObservable.subscribe(this._onSelectedPipeline);
        this.state.settingsAllowedPipelines.settingsObservable.subscribe(this._onPipelinesEditChanged);
        this.state.summaryReportSettingsState.settingsObservable.subscribe(this._testsIncludedInTestSummaryStateChanged);
        this.state.manualMarkUnmarkState.settingsObservable.subscribe(this._onManualMarkUnMarkStateChanged);
        this._flakyPiplineSelection.subscribe(this._onSelectionChange);
        this._store.getPipelines();
    }

    public componentWillUnmount() {
        this.state.settingsToggleButton.settingsObservable.unsubscribe(this._settingsToggleButtonChanged);
        this.state.settingsTypeState.settingsObservable.unsubscribe(this._settingsDetectionTypeStateChanged);
        this.state.selectedPipeline.settingsObservable.unsubscribe(this._onSelectedPipeline);
        this.state.settingsAllowedPipelines.settingsObservable.unsubscribe(this._onPipelinesEditChanged);
        this.state.summaryReportSettingsState.settingsObservable.unsubscribe(this._testsIncludedInTestSummaryStateChanged);
        this.state.manualMarkUnmarkState.settingsObservable.unsubscribe(this._onManualMarkUnMarkStateChanged);
        this._flakyPiplineSelection.unsubscribe(this._onSelectionChange);
        this._store.removeChangedListener(this._handleStoreChange);
        super.componentWillUnmount();
    }

    public render(): JSX.Element[] {
        if (this.state.loading) {
            return [<Spinner className="loading-spinner" label={Resources.LoadingText} key="spinner" />];
        }
        return [
            this._getFlakyDetectionRow(
                Resources.FlakyTestDetectionText,
                Resources.FlakyTestDetectionDescriptionText,
                this._flakyTestDetectionToggleButtonValue
            ),
            this._getSelectPipelinesRow(Resources.SelectPipelineToEnableFlakyTestDetectionText, "", this._flakyTestDetectionToggleButtonValue),
            this._getMarkUnMarkFlakyTestRow(
                Resources.FlakyTestOptionsText,
                "",
                this._flakyTestDetectionToggleButtonValue,
                this._flakyTestsIncludedInTestSummaryValue,
                this._flakyManualMarkUnMarkValue
            ),
            this._getToastBar(Resources.ErrorToastMessage),
            this._getRemovePipelinesRow(),
            this._getFlakyTestDetectionTypeChangeConfirmation()
        ];
    }

    private get _flakyTestDetectionToggleButtonValue(): ObservableValue<boolean> {
        return this.state.settingsToggleButton.settingsObservable;
    }

    private get _flakyDetectionSettingsTypeStateValue(): ObservableValue<string> {
        return this.state.settingsTypeState.settingsObservable;
    }

    private get _flakyTestsIncludedInTestSummaryValue(): ObservableValue<boolean> {
        return this.state.summaryReportSettingsState.settingsObservable;
    }

    private get _flakyManualMarkUnMarkValue(): ObservableValue<boolean> {
        return this.state.manualMarkUnmarkState.settingsObservable;
    }

    private _onSelectionChange = (): void => {
        const selections = this._flakyPiplineSelection.value;

        if (selections && selections.length === 0) {
            if (this._optedSelectPipelineOption === true) {
                this.state.selectedPipeline.settingsObservable.value = Resources.AllPipelinesText;
            }
            return;
        }
        this._optedSelectPipelineOption = true;
        const selectedItems: Set<IListBoxItem<{}>> = new Set();
        for (let i = 0; i < selections.length; i++) {
            for (let j = selections[i].beginIndex; j <= selections[i].endIndex; j++) {
                selectedItems.add(this._pipelineItems.value[j]);
            }
        }

        this._onSelect(Array.from(selectedItems));
    };

    private _onSelect(pipelinesItem: IListBoxItem<{}>[]): void {
        const pipelineIds: number[] = [];
        pipelinesItem.forEach(pipelinesItem => pipelineIds.push(parseInt(pipelinesItem.id)));
        this.state.settingsAllowedPipelines.settingsObservable.value = pipelineIds;
    }

    private _settingsToggleButtonChanged = async (newValue: boolean) => {
        const newValueBool = newValue;
        if (newValueBool !== this.state.settingsToggleButton.settingsData) {
            const testResultsUpdateSettings: TCMContracts.TestResultsUpdateSettings = {
                flakySettings: {} as TCMContracts.FlakySettings,
                newTestResultLoggingSettings: {} as TCMContracts.NewTestResultLoggingSettings
            };
            if (newValueBool) {
                testResultsUpdateSettings.flakySettings.flakyDetection = {
                    flakyDetectionType: TCMContracts.FlakyDetectionType.System,
                    flakyDetectionPipelines: {
                        isAllPipelinesAllowed: true
                    }
                } as TCMContracts.FlakyDetection;
                testResultsUpdateSettings.flakySettings.flakyInSummaryReport = true;
                testResultsUpdateSettings.flakySettings.manualMarkUnmarkFlaky = false;
            }

            const success = await this._store.updateTestResultsSettings(testResultsUpdateSettings);
            if (success) {
                Telemetry.publishEvent(this.context.pageContext, this.context.pageContext, TelemetryConstants.featureFlakyTestProjectSettings, {
                    EnableFlakyTestProjectSettings: newValueBool
                });
            }
        }
    };

    private _settingsDetectionTypeStateChanged = async (newVal: string) => {
        const newValueBool = newVal;
        if (newValueBool !== this.state.settingsTypeState.settingsData) {
            const testResultsUpdateSettings: TCMContracts.TestResultsUpdateSettings = {
                flakySettings: {
                    flakyDetection: {} as TCMContracts.FlakyDetection,
                } as TCMContracts.FlakySettings,
                newTestResultLoggingSettings: {} as TCMContracts.NewTestResultLoggingSettings
            };
            if (newValueBool == Resources.SystemDetectionText) {
                testResultsUpdateSettings.flakySettings.flakyDetection.flakyDetectionType = TCMContracts.FlakyDetectionType.System;
                testResultsUpdateSettings.flakySettings.flakyDetection.flakyDetectionPipelines = {
                    isAllPipelinesAllowed: true
                } as TCMContracts.FlakyDetectionPipelines;
            } else {
                testResultsUpdateSettings.flakySettings.flakyDetection.flakyDetectionType = TCMContracts.FlakyDetectionType.Custom;
            }
            const success = await this._store.updateTestResultsSettings(testResultsUpdateSettings);
            if (success) {
                Telemetry.publishEvent(this.context.pageContext, this.context.pageContext, TelemetryConstants.featureFlakyTestProjectSettings, {
                    FlakyDetectionType: newValueBool
                });
            }
        }
    };

    private _testsIncludedInTestSummaryStateChanged = async (newVal: boolean) => {
        const newValueBool = newVal;
        if (newValueBool !== this.state.summaryReportSettingsState.settingsData) {
            const testResultsUpdateSettings: TCMContracts.TestResultsUpdateSettings = {
                flakySettings: {} as TCMContracts.FlakySettings,
                newTestResultLoggingSettings: {} as TCMContracts.NewTestResultLoggingSettings
            };
            testResultsUpdateSettings.flakySettings.flakyInSummaryReport = newValueBool;
            const success = await this._store.updateTestResultsSettings(testResultsUpdateSettings);
            if (success) {
                Telemetry.publishEvent(this.context.pageContext, this.context.pageContext, TelemetryConstants.featureFlakyTestProjectSettings, {
                    FlakyIncludedInTestSummary: newValueBool
                });
            }
        }
    };

    private _onManualMarkUnMarkStateChanged = async (newVal: boolean) => {
        const newValueBool = newVal;
        if (newValueBool !== this.state.manualMarkUnmarkState.settingsData) {
            const testResultsUpdateSettings: TCMContracts.TestResultsUpdateSettings = {
                flakySettings: {} as TCMContracts.FlakySettings,
                newTestResultLoggingSettings: {} as TCMContracts.NewTestResultLoggingSettings
            };
            testResultsUpdateSettings.flakySettings.manualMarkUnmarkFlaky = newValueBool;
            const success = await this._store.updateTestResultsSettings(testResultsUpdateSettings);
            if (success) {
                Telemetry.publishEvent(this.context.pageContext, this.context.pageContext, TelemetryConstants.featureFlakyTestProjectSettings, {
                    ManualMarkUnMarkFlaky: newValueBool
                });
            }
        }
    };

    private _onPipelinesEditChanged = async (newVal: number[]) => {
        const newValue = newVal;
        if (
            newValue.length > 0 &&
            (newValue.length !== this.state.settingsAllowedPipelines.settingsData.length ||
                newValue.filter(item => this.state.settingsAllowedPipelines.settingsData.indexOf(item) < 0).length > 0)
        ) {
            const testResultsUpdateSettings: TCMContracts.TestResultsUpdateSettings = {
                flakySettings: {} as TCMContracts.FlakySettings,
                newTestResultLoggingSettings: {} as TCMContracts.NewTestResultLoggingSettings
            };
            testResultsUpdateSettings.flakySettings.flakyDetection = {
                flakyDetectionType: TCMContracts.FlakyDetectionType.System,
                flakyDetectionPipelines: {
                    isAllPipelinesAllowed: false,
                    allowedPipelines: newVal
                }
            };
            const success = await this._store.updateTestResultsSettings(testResultsUpdateSettings);
            if (success) {
                Telemetry.publishEvent(this.context.pageContext, this.context.pageContext, TelemetryConstants.featureFlakyTestProjectSettings, {
                    SystemSelectedFlakyPipelines: newValue.length
                });
            }
        }
    };

    private _onSelectedPipeline = async (newVal: string) => {
        const mergeSelections = true;
        const testResultsUpdateSettings: TCMContracts.TestResultsUpdateSettings = {
            flakySettings: {
                flakyDetection: {} as TCMContracts.FlakyDetection
            } as TCMContracts.FlakySettings,
            newTestResultLoggingSettings: {} as TCMContracts.NewTestResultLoggingSettings
        };
        if (newVal !== this.state.selectedPipeline.settingsData && newVal == Resources.AllPipelinesText) {
            this._optedSelectPipelineOption = false;
            testResultsUpdateSettings.flakySettings.flakyDetection = {
                flakyDetectionType: TCMContracts.FlakyDetectionType.System,
                flakyDetectionPipelines: {
                    isAllPipelinesAllowed: true
                }
            } as TCMContracts.FlakyDetection;
            const success = await this._store.updateTestResultsSettings(testResultsUpdateSettings);
            if (success) {
                Telemetry.publishEvent(this.context.pageContext, this.context.pageContext, TelemetryConstants.featureFlakyTestProjectSettings, {
                    SystemSelectedFlakyPipelines: Resources.AllPipelinesText
                });
            }
        } else if (newVal == Resources.SpecificPipelinesText) {
            const selectedIndexes: Set<number> = new Set();
            const selectedPipelines = [] as IListBoxItem<{}>[];
            this.state.settingsAllowedPipelines.settingsData.forEach(pipelineId => {
                const item = this.state.pipelinesMap.settingsData.get(pipelineId.toString());
                if (item != undefined) {
                    const index = this._getPipelinesItems().value.indexOf(item);
                    if (index >= 0) {
                        selectedIndexes.add(index);
                        selectedPipelines.push(item);
                    }
                }
            });

            if (selectedIndexes.size > 0) {
                const pipelinesIds: ISelectionRange[] = [];
                selectedIndexes.forEach(id => {
                    pipelinesIds.push({ beginIndex: id, endIndex: id });
                });
                this._flakyPiplineSelection.value = pipelinesIds;
            }
            if (selectedPipelines.length > 0) {
                this._selectedPipelinesArrayItemProvider = new ArrayItemProvider(selectedPipelines);
            }
        }
    };

    private _getPipelinesItems() {
        this._pipelineItems = new ObservableArray<IListBoxItem<{}>>();
        if (this.state.pipelinesMap.settingsData.size > 0) {
            this._pipelineItems.push({
                id: Resources.SelectPipelinesText,
                text: Resources.SelectPipelinesText,
                type: ListBoxItemType.Header
            });
            this.state.pipelinesMap.settingsData.forEach(item => {
                this._pipelineItems.push(item);
            });
        }
        return this._pipelineItems;
    }

    private _handleStoreChange = (): void => {
        this.setState(this._store.getState());
    };

    private _getFlakyDetectionRow(headerTitle: string, headerDescription: string, flakyDetectionToggleButton: ObservableValue<boolean>) {
        return (
            <CustomCard className="flex-grow bolt-card-with-header settings-card" key={headerTitle}>
                <CustomHeader className="bolt-card-header flex-center bolt-header-with-commandbar">
                    <HeaderTitleArea>
                        <HeaderTitleRow className="settings-header-title-row">
                            <HeaderTitle titleSize={TitleSize.Medium}>{headerTitle}</HeaderTitle>
                            <HeaderDescription>
                                {headerDescription}
                                {
                                    <Link href="https://aka.ms/pipelines_flakytest" target="_blank">
                                        {Resources.LearnMoreText}
                                    </Link>
                                }
                            </HeaderDescription>
                        </HeaderTitleRow>
                    </HeaderTitleArea>
                    <CustomHeaderCommandBar>
                        <Toggle
                            offText={Resources.ToggleOffText}
                            onText={Resources.ToggleOnText}
                            checked={flakyDetectionToggleButton}
                            onChange={(e, newValue) => (flakyDetectionToggleButton.value = newValue)}
                        />
                    </CustomHeaderCommandBar>
                </CustomHeader>
                <CardContent className="setting-pipeline-header">
                    <div className="separator"></div>
                    <div className="flex-column setting-radio-group">
                        <RadioButtonGroup
                            onSelect={selectedId => {
                                if(this._flakyDetectionSettingsTypeStateValue.value != selectedId) {
                                    this._selectedFlakyDetectionType = selectedId;
                                    this.state.isFlakyDetectionTypeChangeDialogOpen.value = true;
                                    this._handleStoreChange();
                                }
                                else{
                                    this._flakyDetectionSettingsTypeStateValue.value = selectedId;
                                    this._handleStoreChange();
                                }
                            }}
                            selectedButtonId={this._flakyDetectionSettingsTypeStateValue.value}
                            text={Resources.SelectDetectionTypeText}
                        >
                            <div className="setting-radio flex-column">
                                <RadioButton
                                    disabled={!flakyDetectionToggleButton.value}
                                    id={Resources.SystemDetectionText}
                                    ariaLabelledBy={"detection-type"}
                                    key={Resources.SystemDetectionText}
                                >
                                    <div className="icon-circle">
                                        <Icon ariaLabel="Rocket icon" iconName="Rocket" />
                                    </div>

                                    <div className="bolt-radio-button-label" id="detection-type">
                                        {Resources.SystemDetectionText}
                                        <span className="secondary-text">{Resources.SystemDetectionDescriptionText}</span>
                                    </div>
                                </RadioButton>
                                <RadioButton
                                    disabled={!flakyDetectionToggleButton.value}
                                    id={Resources.CustomDetectionText}
                                    ariaLabelledBy={"detection-type"}
                                    key={Resources.CustomDetectionText}
                                >
                                    <div className="icon-circle">
                                        <Icon ariaLabel="Repair icon" iconName="Repair" />
                                    </div>
                                    <div className="bolt-radio-button-label" id="detection-type">
                                        {Resources.CustomDetectionText}
                                        <span className="secondary-text">{Resources.CustomDetectionDescriptionText}</span>
                                    </div>
                                </RadioButton>
                            </div>
                        </RadioButtonGroup>
                    </div>
                </CardContent>
            </CustomCard>
        );
    }

    private _getSelectPipelinesRow(headerTitle: string, headerDescription: string, flakyDetectionToggleButton: ObservableValue<boolean>) {
        if (this._flakyDetectionSettingsTypeStateValue.value === Resources.CustomDetectionText) {
            return <div key={headerTitle}> </div>;
        }
        return (
            <CustomCard className="flex-grow bolt-card-with-header settings-card" key={headerTitle}>
                <CustomHeader className="bolt-card-header flex-center bolt-header-with-commandbar">
                    <HeaderTitleArea>
                        <HeaderTitleRow>
                            <HeaderTitle titleSize={TitleSize.Medium}>{headerTitle}</HeaderTitle>
                            <HeaderDescription>{headerDescription}</HeaderDescription>
                        </HeaderTitleRow>
                    </HeaderTitleArea>
                </CustomHeader>
                <CardContent>
                    <div className="flex-column settings-content">
                        <div className="flex-column">
                            <RadioButtonGroup
                                onSelect={selectedId => {
                                    this.state.selectedPipeline.settingsObservable.value = selectedId;
                                    this._handleStoreChange();
                                }}
                                selectedButtonId={this.state.selectedPipeline.settingsObservable}
                            >
                                <div className="setting-radio flex-column">
                                    <RadioButton
                                        disabled={!flakyDetectionToggleButton.value}
                                        id={Resources.AllPipelinesText}
                                        text={Resources.AllPipelinesText}
                                        key={Resources.AllPipelinesText}
                                    />
                                    <RadioButton
                                        disabled={!flakyDetectionToggleButton.value}
                                        id={Resources.SpecificPipelinesText}
                                        text={Resources.SpecificPipelinesText}
                                        key={Resources.SpecificPipelinesText}
                                    />
                                </div>
                            </RadioButtonGroup>
                            {this.state.selectedPipeline.settingsObservable.value === Resources.SpecificPipelinesText &&
                                this._getPipelinesFilterRow()}
                        </div>
                        <div className="flex-column">
                            {this.state.selectedPipeline.settingsObservable.value === Resources.SpecificPipelinesText &&
                                this.state.settingsAllowedPipelines.settingsData.length > 0 &&
                                this._getPipelinesListRow()}
                        </div>
                    </div>
                </CardContent>
            </CustomCard>
        );
    }

    private _getMarkUnMarkFlakyTestRow(
        headerTitle: string,
        headerDescription: string,
        flakyDetectionToggleButton: ObservableValue<boolean>,
        flakyInRunSummary: ObservableValue<boolean>,
        markUnmarkFlaky: ObservableValue<boolean>
    ) {
        return (
            <CustomCard className="flex-grow bolt-card-with-header settings-card setting-card-bottom-margin" key={headerTitle}>
                <CustomHeader className="bolt-card-header flex-center bolt-header-with-commandbar">
                    <HeaderTitleArea>
                        <HeaderTitleRow>
                            <HeaderTitle titleSize={TitleSize.Medium}>{headerTitle}</HeaderTitle>
                            <HeaderDescription>{headerDescription}</HeaderDescription>
                        </HeaderTitleRow>
                    </HeaderTitleArea>
                </CustomHeader>
                <CardContent>
                    <div className="flex-column">
                        <Checkbox
                            className="checkbox-radio-section"
                            disabled={!flakyDetectionToggleButton.value}
                            onChange={(event, newValue) => (flakyInRunSummary.value = newValue)}
                            checked={flakyInRunSummary}
                            label={
                                <div className="flex-column">
                                    {Resources.FlakyTestsIncludedInTestSummaryText}
                                    <div className="secondary-text">{Resources.FlakyTestsIncludedInTestSummaryDescriptionText}</div>
                                </div>
                            }
                        />
                        <Checkbox
                            className="checkbox-radio-section"
                            disabled={!flakyDetectionToggleButton.value}
                            onChange={(event, newValue) => (markUnmarkFlaky.value = newValue)}
                            checked={markUnmarkFlaky}
                            label={
                                <div className="flex-column">
                                    {Resources.ManualMarkUnMarkText}
                                    <div className="secondary-text">{Resources.ManualMarkUnMarkDescriptionText}</div>
                                </div>
                            }
                        />
                    </div>
                </CardContent>
            </CustomCard>
        );
    }

    private _getToastBar(keyLabel: string) {
        if (this.state.serverError) {
            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout(() => {
                return React.createElement(FadeToast, { content: Util_String.localeFormat(this.state.serverMessage) });
            });
        }
        return <div key={keyLabel}></div>;
    }

    private _getRemovePipelinesRow() {
        return (
            <div key={Resources.RemovePipelineText}>
                <Observer isDialogOpen={this._isRemovePipelineDialogOpen}>
                    {(props: { isDialogOpen: boolean }) => {
                        return props.isDialogOpen ? (
                            <Dialog
                                titleProps={{ text: Resources.RemovePipelineText }}
                                footerButtonProps={[
                                    {
                                        text: Resources.CancelText,
                                        onClick: this._onCancelClick
                                    },
                                    {
                                        text: Resources.RemoveText,
                                        onClick: this._onRemovePipelineClick,
                                        primary: true
                                    }
                                ]}
                                onDismiss={this._onCancelClick}
                            >
                                {Resources.RemovePipelineDescriptionText}
                            </Dialog>
                        ) : null;
                    }}
                </Observer>
            </div>
        );
    }

    private _getPipelinesFilterRow() {
        return (
            <div key="add-pipelines" className="add-pipelines">
                <Observer selection={this._flakyPiplineSelection}>
                    {() => {
                        return (
                            <Dropdown
                                actions={[
                                    {
                                        className: "bolt-dropdown-action-right-button",
                                        disabled: this._flakyPiplineSelection.selectedCount === 0,
                                        iconProps: { iconName: "Clear" },
                                        text: Resources.ClearText,
                                        onClick: this._clearPipelineSelection
                                    }
                                ]}
                                items={this._getPipelinesItems()}
                                selection={this._flakyPiplineSelection}
                                placeholder={Resources.AddPipelinesText}
                                showFilterBox={true}
                                noItemsText={Resources.NoPipelinesText}
                                renderItem={(
                                    rowIndex: number,
                                    columnIndex: number,
                                    tableColumn: ITableColumn<IListBoxItem<IListBoxItem<{}>>>,
                                    tableItem: IListBoxItem<IListBoxItem<{}>>
                                ) => (
                                        <SimpleTableCell className="flex-row" columnIndex={columnIndex} key={rowIndex + "" + columnIndex} tableColumn={tableColumn}>
                                            {tableItem.text !== undefined && (
                                                <Tooltip overflowOnly={true}>
                                                    <div className="text-ellipsis">{tableItem.text}</div>
                                                </Tooltip>
                                            )}
                                        </SimpleTableCell>
                                    )}
                            />
                        );
                    }}
                </Observer>
            </div>
        );
    }

    private _clearPipelineSelection = () => {
        this._flakyPiplineSelection.clear();
    };

    private _onRemovePipelineClick = () => {
        if (this._selectedPipelineItem) {
            const index = this._getPipelinesItems().value.indexOf(this._selectedPipelineItem);
            if (index >= 0) {
                this._flakyPiplineSelection.unselect(index, 1);
            }
        }
        this._isRemovePipelineDialogOpen.value = false;
    };

    private _onCancelClick = () => {
        this._isRemovePipelineDialogOpen.value = false;
    };

    private _getPipelinesListRow() {
        return [
            <span key="pipeline-header" className="setting-radio-group">
                {Resources.SelectedPipelinesText}
            </span>,
            <div key="pipeline-separtor" className="separator setting-radio"></div>,
            <div key="pipeline-list" className="flex setting-selected-pipelines setting-radio">
                <ScrollableList itemProvider={this._selectedPipelinesArrayItemProvider} renderRow={this.renderRow} width="100%" />
            </div>
        ];
    }

    private renderRow = (index: number, item: IListBoxItem<{}>, details: IListItemDetails<IListBoxItem<{}>>, key?: string): JSX.Element => {
        return (
            <ListItem key={key || "list-item" + index} index={index} details={details} className="setting-selected-pipeline-header">
                <div className="flex setting-selected-pipelines-row ">
                    <div className="flex-row h-scroll-hidden">
                        <Icon iconName="Rocket" size={IconSize.medium} />
                        <div className="setting-selected-pipelines-icon flex-column h-scroll-hidden">
                            <Tooltip overflowOnly={true}>
                                <div className="text-ellipsis">{item.text}</div>
                            </Tooltip>
                            <span className="fontSizeMS font-size-ms text-ellipsis secondary-text">{Resources.PipelineText}</span>
                        </div>
                    </div>
                    <Button
                        key="pipeline-delete"
                        className="setting-selected-pipelines-delete-btn"
                        iconProps={{ iconName: "Delete", className: "more-icon" }}
                        onClick={() => {
                            this._isRemovePipelineDialogOpen.value = true;
                            this._selectedPipelineItem = item;
                        }}
                        tooltipProps={{ text: Resources.DeletePipelineText }}
                    />
                </div>
            </ListItem>
        );
    };

    private _getFlakyTestDetectionTypeChangeConfirmation() {
        return (
                <Observer isDialogFlakyOpen={this.state.isFlakyDetectionTypeChangeDialogOpen.value}>
                    {(props: { isDialogFlakyOpen: boolean }) => {
                        return props.isDialogFlakyOpen ? (
                                <Dialog
                                    titleProps={{ text: Resources.FlakyTestDetectionTypeChangeText }}
                                    footerButtonProps={[
                                        {
                                            text: Resources.CancelText,
                                            onClick: this._onFlakyCancelClick
                                        },
                                        {
                                            text: Resources.ChangeText,
                                            onClick: this._setFlakyDetectionType,
                                            primary: true
                                        }
                                    ]}
                                    onDismiss={this._onFlakyCancelClick}
                                >
                                    {Resources.FlakyTestDetectionTypeChangeDescriptionText}
                                    <div className="secondary-text">{Resources.DeleteFlakyDataText}</div>
                                </Dialog>
                        ) : null;
                    }}
                </Observer>
        );
    }

    private _onFlakyCancelClick = () => {
        this.state.isFlakyDetectionTypeChangeDialogOpen.value = false;
        Telemetry.publishEvent(this.context.pageContext, this.context.pageContext, TelemetryConstants.featureFlakyDialogBoxCancellation, {
            FlakySettingsUpdateCancelled : true
        });
        this._handleStoreChange();
    };

    private _setFlakyDetectionType = () => {
        this._flakyDetectionSettingsTypeStateValue.value = this._selectedFlakyDetectionType;
        this.state.isFlakyDetectionTypeChangeDialogOpen.value = false;
        this._handleStoreChange();
    };

}

Components.add("pipelines-flaky-test-results-settings-view", TestResultsFlakySettingsView);
